import { CheckCircle, XCircle, CheckSquare, Square } from "lucide-react";
import React from "react";

interface TimeSlot {
  id: string;
  period: string;
  startTime: string;
  endTime: string;
}

interface WeeklyAvailabilityGridProps<T = any> {
  days: string[];

  timeSlots: TimeSlot[];

  availability: T[];

  selectedEntityId: string | null;

  getEntityId: (item: T) => string;

  getIsAvailable: (item: T) => boolean;

  getDay: (item: T) => string;

  getTimeSlotId: (item: T) => string;

  onToggle: (day: string, timeSlotId: string) => void;

  onToggleDay?: (day: string, setAvailable: boolean) => void;

  disabled?: boolean;

  showLegend?: boolean;

  className?: string;
}

const WeeklyAvailabilityGrid: React.FC<WeeklyAvailabilityGridProps> = ({
  days,
  timeSlots,
  availability,
  selectedEntityId,
  getEntityId,
  getIsAvailable,
  getDay,
  getTimeSlotId,
  onToggle,
  onToggleDay,
  disabled = false,
  showLegend = true,
  className = "",
}) => {
  const checkAvailability = (day: string, timeSlotId: string): boolean => {
    if (!selectedEntityId) return false;

    const item = availability.find((a) => getEntityId(a) === selectedEntityId && getDay(a) === day && getTimeSlotId(a) === timeSlotId);
    return item ? getIsAvailable(item) : true;
  };

  const isDayFullyAvailable = (day: string): boolean => {
    if (!selectedEntityId) return false;

    return timeSlots.every((slot) => checkAvailability(day, slot.id));
  };

  const getDayAvailableCount = (day: string): number => {
    if (!selectedEntityId) return 0;

    return timeSlots.filter((slot) => checkAvailability(day, slot.id)).length;
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {showLegend && (
        <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-blue-700">
            <div className="flex items-center space-x-1">
              <CheckCircle size={16} className="text-green-600" />
              <span className="text-sm">Müsait</span>
            </div>
            <span className="text-blue-400">|</span>
            <div className="flex items-center space-x-1">
              <XCircle size={16} className="text-red-600" />
              <span className="text-sm">Müsait Değil</span>
            </div>
            <span className="text-blue-400">|</span>
            <span className="text-sm">Durumu değiştirmek için kutulara tıklayın</span>
            {onToggleDay && (
              <>
                <span className="text-blue-400">|</span>
                <span className="text-sm">Gün başlığına tıklayarak tüm saatleri açın/kapatın</span>
              </>
            )}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <div className="min-w-[800px]">
          <div className="grid bg-gray-50" style={{ gridTemplateColumns: `150px repeat(${days.length}, 1fr)` }}>
            <div className="p-3 text-sm font-medium text-gray-700 border-r border-gray-200">Saat / Gün</div>
            {days.map((day) => {
              const allAvailable = isDayFullyAvailable(day);
              const availableCount = getDayAvailableCount(day);
              const hasToggleDay = onToggleDay && selectedEntityId && !disabled;

              return (
                <div
                  key={day}
                  className={`p-3 text-sm font-medium text-gray-700 text-center border-r border-gray-200 last:border-r-0 ${hasToggleDay ? "cursor-pointer hover:bg-gray-100 transition-colors select-none" : ""}`}
                  onClick={() => {
                    if (hasToggleDay) onToggleDay(day, !allAvailable);
                  }}
                  title={hasToggleDay
                    ? allAvailable
                      ? `${day} - Tüm saatler açık. Tıklayarak tümünü kapatın.`
                      : `${day} - ${availableCount}/${timeSlots.length} saat açık. Tıklayarak tümünü açın.`
                    : undefined
                  }
                >
                  <div className="flex items-center justify-center gap-1.5">
                    <span>{day}</span>
                    {hasToggleDay && (
                      <span className={`inline-flex items-center transition-colors duration-200 ${allAvailable ? "text-green-600" : "text-gray-400"}`}>
                        {allAvailable ? (
                          <CheckSquare size={16} className="drop-shadow-sm" />
                        ) : (
                          <Square size={16} className="drop-shadow-sm" />
                        )}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {timeSlots.map((slot) => (
            <div
              key={slot.id}
              className="grid border-b border-gray-200 last:border-b-0"
              style={{ gridTemplateColumns: `150px repeat(${days.length}, 1fr)` }}>
              <div className="p-3 bg-gray-50 border-r border-gray-200">
                <div className="text-sm font-medium text-gray-900">{slot.period}</div>
                <div className="text-xs text-gray-500">
                  {slot.startTime} - {slot.endTime}
                </div>
              </div>

              {days.map((day) => {
                const isAvailable = checkAvailability(day, slot.id);
                return (
                  <div key={`${day}-${slot.id}`} className="p-2 border-r border-gray-200 last:border-r-0">
                    <button
                      onClick={() => !disabled && onToggle(day, slot.id)}
                      disabled={disabled || !selectedEntityId}
                      className={`w-full h-12 rounded-lg border-2 transition-all duration-200 flex items-center justify-center ${disabled || !selectedEntityId
                        ? "opacity-50 cursor-not-allowed bg-gray-100 border-gray-300"
                        : "hover:scale-105 active:scale-95"
                        } ${isAvailable
                          ? "bg-green-100 border-green-300 text-green-700 hover:bg-green-200 shadow-sm"
                          : "bg-red-100 border-red-300 text-red-700 hover:bg-red-200 shadow-sm"
                        }`}
                      title={!selectedEntityId ? "Önce bir seçim yapın" : disabled ? "Düzenleme devre dışı"
                        : isAvailable
                          ? "✅ Müsait - Kaldırmak için tıklayın"
                          : "❌ Müsait Değil - Eklemek için tıklayın"
                      }>
                      {isAvailable ? <CheckCircle size={20} className="drop-shadow-sm" /> : <XCircle size={20} className="drop-shadow-sm" />}
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WeeklyAvailabilityGrid;